import React, { useEffect, useState } from 'react';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  ApiButton,
  Box,
  ContentTemplate,
  CustomSelect,
  ErrorInfo,
  HeaderInput,
  HeaderTemplate,
  SectionHeader,
  SelectOptionItem,
  Typography,
} from 'web-library';
import {SelectChangeEvent} from '@mui/material';
import { useNotificationReport } from '../logic/useNotificationReport';
import { usePostNotificationReport } from '../../../api-refactored/reports/usePostNotificationReport';
import DownloadAuditProgressModal from '../../../components/modals/DownloadAuditProgressModal';
import { configurationTypeData } from '../logic/dropDownOption';
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";

const useStyles = makeStyles()((theme) =>
({
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      paddingTop: theme.spacing(3),
      marginBottom: theme.spacing(5),
    },
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(2.5),
      marginBottom: theme.spacing(4),
    },
  },
  warningText: {
    width: '100%',
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  formElementContainer: {
    marginBottom: theme.spacing(2),
    width: '30%',
    marginRight: '5%',
    display: 'inline-block',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  singleFormElementContainer: {
    width: '50%',
  },
  errorContainer: {
    display: 'flex',
  },
  errMsg: {
    marginTop: '0 !important',
  },
  label: {
    color: theme.palette.text.secondary,
  },
  title: {
    color: theme.color.text.textHeader,
    marginRight: theme.spacing(2),
  },
  submitButtonContainer: {
    width: '100%',
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: '120px',
  },
  menuItemsContainer: {
    width: '60%',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  chipsContainer: {
    overflowX: 'hidden',
    width: '100%',
  },
  iconButton: {
    position: 'absolute',
    right: '45px',
    top: 'calc(50% - 12px)',
    padding: 0,
  },
  chipLabel: {
    padding: 0,
  },
  inputLabel: {
    marginBottom: theme.spacing(1),
    fontWeight: 700,
    position: 'relative',
    transform: 'none!important',
  },
  rootContainer: {
    display: 'flex',
    alignItems: 'center',
    minHeight: theme.spacing(6),
    padding: '0 !important',
  },
  fromControlContainer: {
    display: 'flex',
    marginBottom: theme.spacing(3),
  },
  radioContainer: {},
}));

const featureTypeOptions: SelectOptionItem[] = [
  { code: 'Email', description: 'Email Logs' },
  {
    code: 'SMS',
    description: 'SMS Logs',
  },
];

const sentStatusOptions: SelectOptionItem[] = [
  { code: 'ALL', description: 'All' },
  { code: 'SUCCESS', description: 'Success' },
  {
    code: 'FAILED',
    description: 'Failed',
  },
];

const NotificationReportDetails = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [sentStatus, setSentStatus] = useState<string | undefined>('All');
  const [featureType, setFeatureType] = useState<string | undefined>('Email');
  const [generateTouched, setGenerateTouched] = useState(false);
  const [sectionHeaderPrefix, setSectionHeaderPrefix] = useState<string | undefined>('Email');

  const featureTypeOptionsDefaultValue = featureTypeOptions[0].code;
  const sentStatusOptionsDefaultValue = sentStatusOptions[0].code;

  const {
    form,
    submitGenerate,
    fileDownloadStatus,
    onDialogClose,
    dialogState,
    process,
  } = useNotificationReport();
  const { handleSubmit, control, formState: { errors }, watch, trigger, setValue } = form;

  usePostNotificationReport();

  const watchStartDate = watch('startDate');
  const watchEndDate = watch('endDate');

  useEffect(() => {
    if ((watchStartDate && generateTouched) || (watchEndDate && generateTouched)) {
      trigger('endDate');
      trigger('startDate');
      trigger('custom');
    }
  }, [watchStartDate, watchEndDate, trigger, generateTouched]);

  const handleSentStatusChange = (e: SelectChangeEvent<unknown>) => {
    setSentStatus(e.target.value as string);
  };
  useEffect(() => {
    setValue('sentStatus', sentStatus);
  }, [sentStatus, setValue]);

  const handleFeatureTypeChange = (e: SelectChangeEvent<unknown>) => {
    setFeatureType(e.target.value as string);
  };
  useEffect(() => {
    setValue('featureType', featureType);
    setSectionHeaderPrefix(featureType);
  }, [featureType, setValue]);

  const validateDateRange = () => {
    if (startDate && endDate) {
      if (dayjs(endDate).diff(startDate, 'day') < 0) {
        form.setError('endDate', {
          type: 'custom',
          message: 'Application End Date should be later than Application Start Date',
        });
        return false;
      }
      if (dayjs(endDate).diff(startDate, 'month', true) > parseFloat('3.0')) {
        form.setError('custom', {
          type: 'manual',
          message: 'Date range should not be more than 3 months',
        });
        return false;
      }
    }
    return true;
  };

  const validateDateRangeHandler = (formValue: any) => {
    const isDateRangeValid = validateDateRange();
    if (!isDateRangeValid) {
      return;
    }
    submitGenerate({ ...formValue });
  };

  const onGenerateClickHandler = async () => {
    if (Object.keys(errors).length === 0 && errors.constructor === Object) {
      setGenerateTouched(true);
    } else {
      setGenerateTouched(false);
    }
  };

  return (
    <>
      <HeaderTemplate>
        <HeaderInput
          label={t('admin->>NotificationReport.NotificationHeader.Feature Type')}
          input={
            <CustomSelect
              menuItems={configurationTypeData(featureTypeOptions)}
              defaultValue={featureTypeOptionsDefaultValue}
              onChange={handleFeatureTypeChange}
              // inputProps={{
              //   inputRef: () => {register('featureType')},
              // }}
              variant="filled"
              isHeader
            />
          }
        />
      </HeaderTemplate>
      <ContentTemplate type="full">
        <SectionHeader
          title={`${sectionHeaderPrefix} ${t('admin->>NotificationReport.ReportDetails')}`}
        />
        <form noValidate onSubmit={handleSubmit(validateDateRangeHandler)}>
          <Box className={classes.formElementContainer}>
            <ControlledDatePicker
              label={t('admin->>NotificationReport.NotificationStartDate')}
              onChange={(date) => {
                setStartDate(date || '');
                setValue('startDate', date  || '');
              }}
              control={control}
              name="startDate"
              disableFuture
              error={!!errors.startDate}
              helperText={errors?.startDate?.message?.toString()}
              inputVariant="filled"
              autoOk
              isRequired
              maxDate={dayjs()}
            />
          </Box>

          <Box className={classes.formElementContainer}>
            <ControlledDatePicker
              label={t('admin->>NotificationReport.NotificationEndDate')}
              onChange={(date) => {
                setEndDate(date || '');
                setValue('endDate', date  || '');
              }}
              control={control}
              name="endDate"
              maxDate={dayjs()}
              disableFuture
              error={!!errors.endDate}
              helperText={errors?.endDate?.message?.toString()}
              inputVariant="filled"
              autoOk
              isRequired
            />
          </Box>

          <Box className={classes.errorContainer}>
            <Typography className={classes.label} variant="subtitle2">
              {t('admin->>NotificationReport.DateRangeMonths', { noOfMonths: 3 })}
            </Typography>
          </Box>
          {errors?.custom?.message !== '' && (
            <Box
              className={[classes.formElementContainer, classes.singleFormElementContainer].join(
                ' '
              )}
            >
              <ErrorInfo label={errors?.custom?.message} />
            </Box>
          )}

          <Box
            className={[classes.formElementContainer, classes.singleFormElementContainer].join(' ')}
          >
            <CustomSelect
              label={t('admin->>NotificationReport.SentStatus')}
              menuItems={configurationTypeData(sentStatusOptions)}
              defaultValue={sentStatusOptionsDefaultValue}
              onChange={handleSentStatusChange}
              // inputProps={{
              //   inputRef: () => {
              //     register('sentStatus');
              //   },
              // }}
            />
          </Box>

          <Box className={classes.submitButtonContainer}>
            <ApiButton status={process} type="submit" onClick={onGenerateClickHandler}>
              {t('admin->>NotificationReport.GenerateReport')}
            </ApiButton>
          </Box>
        </form>
        {/* <LoadingComponent isOpaque /> */}
        <DownloadAuditProgressModal
          fileDownloadStatus={fileDownloadStatus}
          modalState={dialogState}
          onDialogClose={onDialogClose}
        />
      </ContentTemplate>
    </>
  );
};

export default NotificationReportDetails;
